import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, ArrowDown, ArrowUp, CreditCard, Bank, CurrencyBtc, Clock, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function WalletPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('card');
  const [withdrawMethod, setWithdrawMethod] = useState('bank');
  const [processing, setProcessing] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await api.get('/wallet/transactions');
      setTransactions(data);
    } catch {} finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleDeposit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setProcessing(true);
    try {
      const { data } = await api.post('/wallet/deposit', { amount: amt, method });
      toast.success(data.message);
      setAmount('');
      await refreshUser();
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deposit failed');
    } finally { setProcessing(false); }
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setProcessing(true);
    try {
      const { data } = await api.post('/wallet/withdraw', { amount: amt, method: withdrawMethod });
      toast.success(data.message);
      setAmount('');
      await refreshUser();
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally { setProcessing(false); }
  };

  const MethodIcon = ({ m }) => {
    if (m === 'card') return <CreditCard size={16} />;
    if (m === 'bank') return <Bank size={16} />;
    if (m === 'crypto') return <CurrencyBtc size={16} />;
    return <Wallet size={16} />;
  };

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 pb-12" style={{ backgroundColor: 'var(--oh-bg)' }} data-testid="wallet-page">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/markets')} className="flex items-center gap-2 mb-6 mt-4 transition-colors" style={{ color: 'var(--oh-text-secondary)' }} data-testid="wallet-back">
          <ArrowLeft size={18} /> Back to Markets
        </button>

        {/* Balance Card */}
        <div className="rounded-2xl p-8 mb-8 border relative overflow-hidden" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }} data-testid="wallet-balance-card">
          <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,255,163,0.05)] to-[rgba(0,184,255,0.05)] pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={22} className="text-[#00FFA3]" weight="fill" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--oh-text-secondary)' }}>Available Balance</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border border-[rgba(0,255,163,0.2)]">Simulated</span>
            </div>
            <p className="font-mono text-5xl font-bold" style={{ color: 'var(--oh-text)' }} data-testid="wallet-balance-display">
              ${(user?.wallet_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Deposit / Withdraw Panel */}
          <div className="rounded-xl p-6 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
            <div className="flex gap-2 mb-6">
              <button onClick={() => { setTab('deposit'); setAmount(''); }} data-testid="tab-deposit"
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                  tab === 'deposit' ? 'bg-[#00FFA3] text-black' : 'text-[var(--oh-text-secondary)]'
                }`} style={tab !== 'deposit' ? { backgroundColor: 'var(--oh-bg-secondary)' } : {}}>
                <ArrowDown size={16} weight="bold" /> Deposit
              </button>
              <button onClick={() => { setTab('withdraw'); setAmount(''); }} data-testid="tab-withdraw"
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                  tab === 'withdraw' ? 'bg-[#00B8FF] text-black' : 'text-[var(--oh-text-secondary)]'
                }`} style={tab !== 'withdraw' ? { backgroundColor: 'var(--oh-bg-secondary)' } : {}}>
                <ArrowUp size={16} weight="bold" /> Cash Out
              </button>
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.2em] font-bold mb-2 block" style={{ color: 'var(--oh-text-secondary)' }}>Amount ($)</label>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder={tab === 'deposit' ? 'Enter deposit amount' : 'Enter withdrawal amount'}
                min={tab === 'withdraw' ? '10' : '1'} max={tab === 'deposit' ? '100000' : undefined} step="0.01"
                className="h-12 font-mono text-lg" data-testid="wallet-amount-input"
                style={{ backgroundColor: 'var(--oh-bg)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }} />
              <div className="flex gap-2 mt-2">
                {(tab === 'deposit' ? [100, 500, 1000, 5000] : [50, 100, 500, 1000]).map(amt => (
                  <button key={amt} onClick={() => setAmount(String(amt))} data-testid={`quick-${amt}`}
                    className="flex-1 py-1.5 text-xs font-mono rounded transition-colors border"
                    style={{ backgroundColor: 'var(--oh-bg)', borderColor: 'var(--oh-border)', color: 'var(--oh-text-secondary)' }}>
                    ${amt.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-5">
              <label className="text-xs uppercase tracking-[0.2em] font-bold mb-2 block" style={{ color: 'var(--oh-text-secondary)' }}>
                {tab === 'deposit' ? 'Payment Method' : 'Withdrawal Method'}
              </label>
              {tab === 'deposit' ? (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'card', label: 'Card', icon: CreditCard },
                    { value: 'bank', label: 'Bank', icon: Bank },
                    { value: 'crypto', label: 'Crypto', icon: CurrencyBtc },
                  ].map(m => (
                    <button key={m.value} onClick={() => setMethod(m.value)} data-testid={`method-${m.value}`}
                      className={`py-3 rounded-lg text-sm font-medium flex flex-col items-center gap-1.5 border transition-all ${
                        method === m.value ? 'border-[#00FFA3] text-[#00FFA3] bg-[rgba(0,255,163,0.05)]' : ''
                      }`} style={method !== m.value ? { borderColor: 'var(--oh-border)', color: 'var(--oh-text-secondary)', backgroundColor: 'var(--oh-bg)' } : {}}>
                      <m.icon size={20} weight={method === m.value ? 'fill' : 'regular'} />
                      {m.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'bank', label: 'Bank Transfer', icon: Bank },
                    { value: 'crypto', label: 'Crypto Wallet', icon: CurrencyBtc },
                  ].map(m => (
                    <button key={m.value} onClick={() => setWithdrawMethod(m.value)} data-testid={`withdraw-method-${m.value}`}
                      className={`py-3 rounded-lg text-sm font-medium flex flex-col items-center gap-1.5 border transition-all ${
                        withdrawMethod === m.value ? 'border-[#00B8FF] text-[#00B8FF] bg-[rgba(0,184,255,0.05)]' : ''
                      }`} style={withdrawMethod !== m.value ? { borderColor: 'var(--oh-border)', color: 'var(--oh-text-secondary)', backgroundColor: 'var(--oh-bg)' } : {}}>
                      <m.icon size={20} weight={withdrawMethod === m.value ? 'fill' : 'regular'} />
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={tab === 'deposit' ? handleDeposit : handleWithdraw} disabled={processing || !amount}
              className={`w-full h-12 font-bold text-black text-lg ${tab === 'deposit' ? 'bg-[#00FFA3] hover:shadow-[0_0_20px_rgba(0,255,163,0.3)]' : 'bg-[#00B8FF] hover:shadow-[0_0_20px_rgba(0,184,255,0.3)]'} transition-all`}
              data-testid="wallet-submit-button">
              {processing ? 'Processing...' : tab === 'deposit' ? 'Deposit Funds' : 'Cash Out'}
            </Button>
            <p className="text-xs text-center mt-3" style={{ color: 'var(--oh-text-muted)' }}>
              {tab === 'deposit' ? 'Simulated payment. Funds added instantly.' : 'Simulated withdrawal. Min $10.'}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="space-y-4">
            <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
              <h3 className="font-heading text-base mb-4" style={{ color: 'var(--oh-text)' }}>Quick Actions</h3>
              <div className="space-y-2">
                <Button onClick={async () => { await api.post('/wallet/topup'); await refreshUser(); fetchTransactions(); toast.success('$1,000 bonus added!'); }}
                  className="w-full bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold h-11" data-testid="quick-topup">
                  <Wallet size={16} className="mr-2" weight="fill" /> Quick Top-Up ($1,000)
                </Button>
                <Button onClick={() => navigate('/portfolio')} variant="outline" className="w-full h-11 border"
                  style={{ borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
                  View Portfolio
                </Button>
                <Button onClick={() => navigate('/markets')} variant="outline" className="w-full h-11 border"
                  style={{ borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
                  Trade Markets
                </Button>
              </div>
            </div>
            <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
              <h3 className="font-heading text-base mb-3" style={{ color: 'var(--oh-text)' }}>Payment Methods</h3>
              <div className="space-y-2 text-sm" style={{ color: 'var(--oh-text-secondary)' }}>
                <div className="flex items-center gap-2"><CreditCard size={16} className="text-[#00FFA3]" /> Visa, Mastercard, Amex</div>
                <div className="flex items-center gap-2"><Bank size={16} className="text-[#00B8FF]" /> Bank wire (SWIFT, ACH)</div>
                <div className="flex items-center gap-2"><CurrencyBtc size={16} className="text-[#F59E0B]" /> BTC, ETH, USDC, USDT</div>
              </div>
              <p className="text-xs mt-3" style={{ color: 'var(--oh-text-muted)' }}>All payments simulated for MVP</p>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div>
          <h2 className="font-heading text-xl mb-4" style={{ color: 'var(--oh-text)' }}>Transaction History</h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#00FFA3] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="rounded-xl p-12 text-center border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
              <Clock size={48} className="mx-auto mb-4" style={{ color: 'var(--oh-text-muted)' }} />
              <p className="text-lg" style={{ color: 'var(--oh-text-secondary)' }}>No transactions yet</p>
              <p className="text-sm" style={{ color: 'var(--oh-text-muted)' }}>Your deposits and withdrawals will appear here.</p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
              {transactions.map((tx) => {
                const isDeposit = tx.type === 'deposit' || tx.type === 'topup';
                const icon = isDeposit ? ArrowDown : ArrowUp;
                const color = isDeposit ? '#00FFA3' : '#00B8FF';
                const Icon = icon;
                return (
                  <div key={tx.id} className="flex items-center gap-4 px-5 py-4 border-b last:border-0 transition-colors" style={{ borderColor: 'var(--oh-border)' }}
                    data-testid={`tx-${tx.id}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                      <Icon size={18} style={{ color }} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--oh-text)' }}>{tx.description}</p>
                      <p className="text-xs flex items-center gap-2" style={{ color: 'var(--oh-text-muted)' }}>
                        <MethodIcon m={tx.method} /> {formatDate(tx.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-bold" style={{ color }}>
                        {isDeposit ? '+' : '-'}${tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs flex items-center gap-1 justify-end" style={{ color: 'var(--oh-text-muted)' }}>
                        <CheckCircle size={10} weight="fill" className="text-[#00FFA3]" /> {tx.status}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
